import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import { Box, Grid, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { UseMutationResult } from 'react-query'
import { getColorForDepositStatus, getReadableDepositStatus } from '../../../domain/Adopters/AccountDisplay'
import {
  AdopterDto,
  AdoptionAttemptDto,
  AdoptionAttemptDtoSterilizationDepositStatus,
  AnimalDto,
  AnimalDtoSpecies,
} from '../../../interactors/gen/backendClient'
import { isDoneOutsidePetsoAttempt } from '../../../utils/adoptionAttemptOrigin'
import { isValidPastDateCoherence } from '../../../utils/date/isValidPastDateCoherence'
import { useFormExtended } from '../../../utils/hooks/useFormExtended'
import { AnimalCardSaveButton } from '../../Animals/Details/AnimalCardSaveButton'
import { CollapsableCard } from '../../common/CollapsableCard'
import { ColoredChipSelectField } from '../../common/ColoredChipSelectField'
import { ControlledDateField } from '../../common/ControlledDateField'
import { colors } from '../../theme'
import { ExitDocumentCard } from './ExitDocumentCard'
import { FileCard } from './FileCard'

interface FormParams {
  needed: string
  depositAmount: number
  depositStatus?: string
  date: string
}

interface Props {
  adopter: AdopterDto
  setAdoptionAttempt: React.Dispatch<React.SetStateAction<AdoptionAttemptDto | undefined>>
  adoptionAttempt: AdoptionAttemptDto
  updateAttemptMutation: UseMutationResult<
    AdoptionAttemptDto,
    Error | AxiosError<unknown, unknown>,
    AdoptionAttemptDto,
    unknown
  >
  isReader: boolean
  animal?: AnimalDto
}

export const SterilizationCard: React.FC<Props> = ({
  adopter,
  setAdoptionAttempt,
  adoptionAttempt,
  updateAttemptMutation,
  isReader,
  animal,
}) => {
  const [isLoading, setIsLoading] = React.useState(false)

  const isFerretAlreadySterilized =
    animal?.species === AnimalDtoSpecies.Ferret &&
    (animal?.health?.sterilizedForFerrets === 'sterilized' || animal?.health?.sterilizedForFerrets === 'implanted')

  const isAlreadySterilizedByAnimal = animal?.health?.sterilizedOrNeutered === 'true' || isFerretAlreadySterilized
  const isPastAdoptionOutsidePetso = isDoneOutsidePetsoAttempt(adoptionAttempt)

  const getDefaultValues = (attempt: AdoptionAttemptDto): FormParams => ({
    needed: isAlreadySterilizedByAnimal
      ? 'false'
      : attempt?.sterilization?.needed === true
      ? 'true'
      : attempt?.sterilization?.needed === false
      ? 'false'
      : '',
    depositAmount: attempt?.sterilization?.depositAmount || 0,
    depositStatus: attempt?.sterilization?.depositStatus,
    date: attempt?.sterilization?.date || '',
  })

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useFormExtended(adoptionAttempt, getDefaultValues)

  const onSubmit = async (data: FormParams) => {
    setIsLoading(true)

    const sterilization =
      data.needed === ''
        ? undefined
        : {
            ...adoptionAttempt.sterilization,
            needed: data.needed === 'true',
            depositAmount: data.depositAmount,
            depositStatus: data.depositStatus as AdoptionAttemptDtoSterilizationDepositStatus,
            date: data.date,
          }

    const updatedAdoptionAttempt = {
      ...adoptionAttempt,
      sterilization,
    }

    await updateAttemptMutation.mutateAsync(updatedAdoptionAttempt)
    setIsLoading(false)
  }

  useEffect(() => {
    if (!adoptionAttempt) return
    const defaultValues = getDefaultValues(adoptionAttempt)
    if (defaultValues.needed === 'false' && adoptionAttempt.sterilization?.needed === true) {
      const updatedAdoptionAttempt = {
        ...adoptionAttempt,
        sterilization: {
          ...adoptionAttempt.sterilization,
          needed: false,
        },
      }
      updateAttemptMutation.mutateAsync(updatedAdoptionAttempt)
    }
  }, [adoptionAttempt?.id])

  const sterilizationStatus = (() => {
    if (isAlreadySterilizedByAnimal) {
      return { label: 'Non requise', color: '#EEF2F7' }
    }

    if (adoptionAttempt.sterilization?.needed === true) {
      return { label: 'Demandée', color: '#DCEAF8' }
    }

    if (adoptionAttempt.sterilization?.needed === false) {
      return { label: 'Non demandée', color: '#EEF2F7' }
    }

    return isPastAdoptionOutsidePetso ? { label: 'À compléter', color: '#F8E9C7' } : undefined
  })()

  return (
    <CollapsableCard
      title="Stérilisation"
      statusLabel={sterilizationStatus?.label}
      statusColor={sterilizationStatus?.color}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          marginTop: 16,
          width: '100%',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
              <Tooltip
                title={
                  isAlreadySterilizedByAnimal
                    ? 'L’animal est déjà stérilisé, il n’est pas nécessaire de demander une stérilisation à l’adoptant.'
                    : 'Indiquez si la stérilisation est nécessaire pour cet animal. Si oui, vous pourrez ensuite demander une caution et renseigner la date prévue de stérilisation.'
                }
              >
                <IconButton size="small" sx={{ color: colors.cello, p: 0.35 }}>
                  <HelpOutlineRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <ColoredChipSelectField
              control={control}
              error={errors.needed}
              fieldName="needed"
              label="Stérilisation par l'adoptant"
              options={[
                { label: 'Oui', value: 'true', color: colors.seaGreen },
                { label: 'Non', value: 'false', color: colors.amber },
              ]}
              requiredRule=""
              size="small"
              disabled={isAlreadySterilizedByAnimal}
              sx={{ mb: 2 }}
            />
            <ControlledDateField
              control={control}
              error={errors.date}
              fieldName="date"
              label="Date de stérilisation prévue"
              requiredRule=""
              size="small"
              validate={(value) => isValidPastDateCoherence(value) || value === null}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <TextField
              id="price-input"
              label="Montant de la caution"
              type="number"
              inputProps={{
                step: '0.01',
                inputMode: 'decimal',
                pattern: '[0-9]*',
              }}
              fullWidth
              {...register('depositAmount')}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ mb: 2 }}
            />
            <ColoredChipSelectField
              control={control}
              error={errors.depositStatus}
              fieldName="depositStatus"
              label="Etat de la caution"
              options={Object.values(AdoptionAttemptDtoSterilizationDepositStatus).map((status) => ({
                label: getReadableDepositStatus(status),
                value: status,
                color: getColorForDepositStatus(status),
              }))}
              requiredRule=""
              size="small"
              sx={{ mb: 2, mt: 0.4 }}
            />
            <AnimalCardSaveButton disabled={!isDirty} isLoading={isLoading} />
          </Grid>
          <Grid item xs={6} md={6}>
            {!adoptionAttempt.sterilization?.file ? (
              <FileCard
                title={`Certificat de stérilisation`}
                type="sterilization"
                originalFileKey={undefined}
                skipProcedure
                description={`L'adoptant vous transmettra le certificat de stérilisation de l'animal. Vous pourrez le télécharger ici.`}
                adoptionAttemptId={adoptionAttempt.id}
                adopter={adopter}
                setAdoptionAttempt={setAdoptionAttempt}
                disabled={isReader}
              />
            ) : !adoptionAttempt.sterilization?.validated ? (
              <FileCard
                title="Certificat de stérilisation à valider"
                description={`${adopter.firstName} a transmis le certificat de stérilisation le ${dayjs(
                  adoptionAttempt.sterilization?.documentSentDate
                ).format('DD/MM/YYYY')}, vous pouvez le télécharger ci-dessous et le valider`}
                fileKey={adoptionAttempt.sterilization.file}
                type="sterilization"
                validation
                adopter={adopter}
                adoptionAttemptId={adoptionAttempt.id}
                adoptionAttempt={adoptionAttempt}
                setAdoptionAttempt={setAdoptionAttempt}
                disabled={isReader}
                updateAttemptMutation={updateAttemptMutation}
              />
            ) : (
              <ExitDocumentCard
                title={'Certificat de stérilisation validé'}
                type={'others'}
                adopterId={adopter.id}
                adoptionAttemptId={adoptionAttempt.id}
                originalFileKey={adoptionAttempt.sterilization.file || ''}
                disabled={isReader}
              />
            )}
          </Grid>
        </Grid>
      </form>
    </CollapsableCard>
  )
}
